import { useSyncExternalStore } from 'react';

import { AdminApiFailure, adminApi, type RequestListItem } from './api';

const POLL_INTERVAL = 5_000;

function errorText(error: unknown): string {
  return error instanceof AdminApiFailure
    ? error.message
    : 'The console hit an unexpected problem.';
}

interface PendingRequestsSnapshot {
  requests: RequestListItem[];
  error: string | null;
}

class PendingRequestsStore {
  private requests: RequestListItem[] = [];
  private error: string | null = null;
  private snapshot: PendingRequestsSnapshot = { requests: [], error: null };
  private listeners = new Set<() => void>();
  private inflight: Promise<void> | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private subscriberCount = 0;
  private visibilityBound: (() => void) | null = null;
  private generation = 0;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    this.subscriberCount++;
    if (this.subscriberCount === 1) {
      this.start();
    }
    return () => {
      this.listeners.delete(listener);
      this.subscriberCount--;
      if (this.subscriberCount === 0) {
        this.stop();
      }
    };
  };

  getSnapshot = (): PendingRequestsSnapshot => this.snapshot;

  private commit() {
    this.snapshot = { requests: this.requests, error: this.error };
    for (const listener of this.listeners) {
      listener();
    }
  }

  private start() {
    void this.fetch();
    this.intervalId = setInterval(() => void this.fetch(), POLL_INTERVAL);
    this.visibilityBound = this.onVisibilityChange;
    document.addEventListener('visibilitychange', this.visibilityBound);
  }

  private stop() {
    this.pauseInterval();
    if (this.visibilityBound) {
      document.removeEventListener('visibilitychange', this.visibilityBound);
      this.visibilityBound = null;
    }
  }

  private pauseInterval() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private resumeInterval() {
    if (this.intervalId === null) {
      void this.fetch();
      this.intervalId = setInterval(() => void this.fetch(), POLL_INTERVAL);
    }
  }

  private onVisibilityChange = () => {
    if (document.hidden) {
      this.pauseInterval();
    } else {
      this.resumeInterval();
    }
  };

  fetch = async (): Promise<void> => {
    if (this.inflight) return this.inflight;
    const gen = this.generation;
    this.inflight = (async () => {
      try {
        const result = await adminApi.requests('pending');
        if (gen !== this.generation) return;
        this.requests = result.requests;
        this.error = null;
      } catch (caught) {
        if (gen !== this.generation) return;
        this.error = errorText(caught);
      } finally {
        if (gen === this.generation) {
          this.inflight = null;
          this.commit();
        }
      }
    })();
    return this.inflight;
  };

  refresh = async (): Promise<void> => {
    return this.fetch();
  };

  reset() {
    this.generation++;
    this.stop();
    this.requests = [];
    this.error = null;
    this.inflight = null;
    this.listeners.clear();
    this.subscriberCount = 0;
    this.snapshot = { requests: [], error: null };
  }
}

export const pendingRequestsStore = new PendingRequestsStore();

export function refreshPendingRequests(): Promise<void> {
  return pendingRequestsStore.refresh();
}

export function usePendingRequests(): {
  requests: RequestListItem[];
  error: string | null;
  refresh: () => Promise<void>;
} {
  const snapshot = useSyncExternalStore(
    pendingRequestsStore.subscribe,
    pendingRequestsStore.getSnapshot,
    pendingRequestsStore.getSnapshot,
  );
  return {
    requests: snapshot.requests,
    error: snapshot.error,
    refresh: pendingRequestsStore.refresh,
  };
}
